/**
 * Settings Page - Company and Invoice Settings
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, LoadingSpinner } from '../components/ui';
import { useSettings, useUpdateSettings } from '../hooks/useSettings';
import { useToast } from '../components/ui/Toast';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { data: settings, isLoading, error } = useSettings();
  const updateSettings = useUpdateSettings();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'company' | 'invoice' | 'reminder' | 'animation'>('company');

  // Form state
  const [companyForm, setCompanyForm] = useState({
    name: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'DE',
    tax_id: '',
    vat_id: '',
    bankName: '',
    iban: '',
    bic: '',
  });

  // Animation settings (localStorage-based)
  const [animSpeed, setAnimSpeed] = useState(() => {
    const saved = localStorage.getItem('animation_speed');
    return saved ? parseFloat(saved) : 0.002;
  });
  const [animParticleX, setAnimParticleX] = useState(() => {
    const saved = localStorage.getItem('animation_particle_x');
    return saved ? parseInt(saved) : 80;
  });
  const [animParticleZ, setAnimParticleZ] = useState(() => {
    const saved = localStorage.getItem('animation_particle_z');
    return saved ? parseInt(saved) : 40;
  });
  const [animWave, setAnimWave] = useState(() => {
    const saved = localStorage.getItem('animation_wave');
    return saved ? parseFloat(saved) : 2.5;
  });
  const [animNoise, setAnimNoise] = useState(() => {
    const saved = localStorage.getItem('animation_noise');
    return saved ? parseFloat(saved) : 1.2;
  });

  const [invoiceForm, setInvoiceForm] = useState({
    default_payment_days: 14,
    vat_rate: 19,
    number_format: 'YYYY-NNNN',
  });

  // Initialize form when settings load
  useState(() => {
    if (settings) {
      setCompanyForm({
        name: settings.company.name || '',
        street: settings.company.address.street || '',
        postalCode: settings.company.address.postalCode || '',
        city: settings.company.address.city || '',
        country: settings.company.address.country || 'DE',
        tax_id: settings.company.tax_id || '',
        vat_id: settings.company.vat_id || '',
        bankName: settings.company.bank_account.bankName || '',
        iban: settings.company.bank_account.iban || '',
        bic: settings.company.bank_account.bic || '',
      });
      setInvoiceForm({
        default_payment_days: settings.invoice.default_payment_days,
        vat_rate: settings.invoice.vat_rate,
        number_format: settings.invoice.number_format,
      });
    }
  });

  const handleSaveCompany = async () => {
    try {
      await updateSettings.mutateAsync({
        company: {
          name: companyForm.name,
          address: {
            street: companyForm.street,
            postalCode: companyForm.postalCode,
            city: companyForm.city,
            country: companyForm.country,
          },
          tax_id: companyForm.tax_id,
          vat_id: companyForm.vat_id,
          bank_account: {
            bankName: companyForm.bankName,
            iban: companyForm.iban,
            bic: companyForm.bic,
          },
        },
      });
      addToast('success', 'Firmendaten gespeichert');
    } catch {
      addToast('error', 'Fehler beim Speichern');
    }
  };

  const handleSaveInvoice = async () => {
    try {
      await updateSettings.mutateAsync({
        invoice: invoiceForm,
      });
      addToast('success', 'Rechnungseinstellungen gespeichert');
    } catch {
      addToast('error', 'Fehler beim Speichern');
    }
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <AppShell>
          <GlassPanel>
            <div className="text-center py-8">
              <p className="text-red-400">Fehler beim Laden der Einstellungen</p>
            </div>
          </GlassPanel>
        </AppShell>
      </ProtectedRoute>
    );
  }

  const tabs = [
    { id: 'company', label: 'Firmendaten' },
    { id: 'invoice', label: 'Rechnungen' },
    { id: 'reminder', label: 'Mahnungen' },
    { id: 'animation', label: 'Animation' },
  ] as const;

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white">Einstellungen</h1>
          <p className="text-white/50 mt-2">Systemkonfiguration und Firmendaten</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Company Settings */}
        {activeTab === 'company' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Firmeninformationen</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Input
                    label="Firmenname"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    placeholder="Ihre Firma GmbH"
                  />
                </div>
                <Input
                  label="Straße und Hausnummer"
                  value={companyForm.street}
                  onChange={(e) => setCompanyForm({ ...companyForm, street: e.target.value })}
                  placeholder="Musterstraße 1"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="PLZ"
                    value={companyForm.postalCode}
                    onChange={(e) => setCompanyForm({ ...companyForm, postalCode: e.target.value })}
                    placeholder="12345"
                  />
                  <div className="col-span-2">
                    <Input
                      label="Stadt"
                      value={companyForm.city}
                      onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                      placeholder="Berlin"
                    />
                  </div>
                </div>
              </div>
            </GlassPanel>

            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Steuerinformationen</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input
                  label="Steuernummer"
                  value={companyForm.tax_id}
                  onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                  placeholder="12/345/67890"
                />
                <Input
                  label="USt-IdNr."
                  value={companyForm.vat_id}
                  onChange={(e) => setCompanyForm({ ...companyForm, vat_id: e.target.value })}
                  placeholder="DE123456789"
                />
              </div>
            </GlassPanel>

            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Bankverbindung</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Input
                    label="Bank"
                    value={companyForm.bankName}
                    onChange={(e) => setCompanyForm({ ...companyForm, bankName: e.target.value })}
                    placeholder="Deutsche Bank"
                  />
                </div>
                <Input
                  label="IBAN"
                  value={companyForm.iban}
                  onChange={(e) => setCompanyForm({ ...companyForm, iban: e.target.value })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
                <Input
                  label="BIC"
                  value={companyForm.bic}
                  onChange={(e) => setCompanyForm({ ...companyForm, bic: e.target.value })}
                  placeholder="COBADEFFXXX"
                />
              </div>
            </GlassPanel>

            <div className="flex justify-end">
              <Button onClick={handleSaveCompany} loading={updateSettings.isPending}>
                Firmendaten speichern
              </Button>
            </div>
          </div>
        )}

        {/* Invoice Settings */}
        {activeTab === 'invoice' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Rechnungseinstellungen</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input
                  label="Standard-Zahlungsziel (Tage)"
                  type="number"
                  min={0}
                  value={invoiceForm.default_payment_days}
                  onChange={(e) => {
                    const days = Number.parseInt(e.target.value, 10);
                    setInvoiceForm({
                      ...invoiceForm,
                      default_payment_days: Number.isNaN(days) ? 14 : days,
                    });
                  }}
                />
                <Input
                  label="Standard-MwSt. (%)"
                  type="number"
                  value={invoiceForm.vat_rate}
                  onChange={(e) =>
                    setInvoiceForm({ ...invoiceForm, vat_rate: parseInt(e.target.value) || 19 })
                  }
                />
                <div className="md:col-span-2">
                  <Input
                    label="Rechnungsnummer-Format"
                    value={invoiceForm.number_format}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, number_format: e.target.value })}
                    hint="YYYY = Jahr, NNNN = fortlaufende Nummer"
                  />
                </div>
              </div>
            </GlassPanel>

            <div className="flex justify-end">
              <Button onClick={handleSaveInvoice} loading={updateSettings.isPending}>
                Rechnungseinstellungen speichern
              </Button>
            </div>
          </div>
        )}

        {/* Animation Settings */}
        {activeTab === 'animation' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Hintergrund-Animation</h2>
              <p className="text-white/50 mb-6 text-sm">
                Passe die Parameter der Partikel-Animation an
              </p>
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Geschwindigkeit
                    <span className="ml-3 text-white/40 font-normal">{animSpeed.toFixed(4)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.0001"
                    max="0.01"
                    step="0.0001"
                    value={animSpeed}
                    onChange={(e) => setAnimSpeed(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Langsam</span>
                    <span>Schnell</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Partikel Breite (X-Achse)
                    <span className="ml-3 text-white/40 font-normal">{animParticleX}</span>
                  </label>
                  <input
                    type="range"
                    min="20"
                    max="150"
                    step="5"
                    value={animParticleX}
                    onChange={(e) => setAnimParticleX(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <p className="text-xs text-white/40 mt-1">Mehr Partikel = höhere GPU-Last</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Partikel Tiefe (Z-Achse)
                    <span className="ml-3 text-white/40 font-normal">{animParticleZ}</span>
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="80"
                    step="5"
                    value={animParticleZ}
                    onChange={(e) => setAnimParticleZ(parseInt(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Wellen-Amplitude
                    <span className="ml-3 text-white/40 font-normal">{animWave.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="5"
                    step="0.1"
                    value={animWave}
                    onChange={(e) => setAnimWave(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Flach</span>
                    <span>Wellig</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-3">
                    Rausch-Intensität (Bewegung)
                    <span className="ml-3 text-white/40 font-normal">{animNoise.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={animNoise}
                    onChange={(e) => setAnimNoise(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/90 [&::-webkit-slider-thumb]:cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-1">
                    <span>Ruhig</span>
                    <span>Bewegt</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/[0.06]">
                <Button
                  onClick={() => {
                    localStorage.setItem('animation_speed', animSpeed.toString());
                    localStorage.setItem('animation_particle_x', animParticleX.toString());
                    localStorage.setItem('animation_particle_z', animParticleZ.toString());
                    localStorage.setItem('animation_wave', animWave.toString());
                    localStorage.setItem('animation_noise', animNoise.toString());
                    window.location.reload();
                  }}
                >
                  Speichern & Neu laden
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAnimSpeed(0.002);
                    setAnimParticleX(80);
                    setAnimParticleZ(40);
                    setAnimWave(2.5);
                    setAnimNoise(1.2);
                    localStorage.removeItem('animation_speed');
                    localStorage.removeItem('animation_particle_x');
                    localStorage.removeItem('animation_particle_z');
                    localStorage.removeItem('animation_wave');
                    localStorage.removeItem('animation_noise');
                    window.location.reload();
                  }}
                >
                  Zurücksetzen
                </Button>
              </div>
            </GlassPanel>
          </div>
        )}

        {/* Reminder Settings */}
        {activeTab === 'reminder' && (
          <div className="space-y-6">
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-6">Mahnungseinstellungen</h2>
              <p className="text-white/50 mb-6">
                Konfigurieren Sie die automatischen Mahnungsstufen und Gebühren.
              </p>

              <div className="space-y-6">
                {/* Level 1 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">1. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach Fälligkeit"
                      type="number"
                      value={settings?.reminder.level_1_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_1_fee || 5}
                      disabled
                    />
                  </div>
                </div>

                {/* Level 2 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">2. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach 1. Mahnung"
                      type="number"
                      value={settings?.reminder.level_2_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_2_fee || 10}
                      disabled
                    />
                  </div>
                </div>

                {/* Level 3 */}
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <h3 className="font-medium text-white mb-4">3. Mahnung</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Tage nach 2. Mahnung"
                      type="number"
                      value={settings?.reminder.level_3_days || 14}
                      disabled
                    />
                    <Input
                      label="Mahngebühr (€)"
                      type="number"
                      value={settings?.reminder.level_3_fee || 15}
                      disabled
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <Input
                    label="Verzugszinsen (% p.a.)"
                    type="number"
                    value={settings?.reminder.interest_rate || 5}
                    disabled
                    hint="Wird automatisch auf überfällige Beträge angewendet"
                  />
                </div>
              </div>

              <p className="text-white/40 text-sm mt-6">
                Mahnungseinstellungen können derzeit nicht geändert werden.
              </p>
            </GlassPanel>
          </div>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
